import React from 'react';
import { ExternalLink, Link2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { useQuickBooks } from '../../../core/hooks/useQuickBooks';
import { EmptyState } from '../../../components/shared/EmptyState';

interface ClientQuickBooksTabProps {
  clientId: string;
  currentCompanyId?: string;
}

export const ClientQuickBooksTab: React.FC<ClientQuickBooksTabProps> = ({
  clientId,
  currentCompanyId,
}) => {
  const { config, linkClientToCompany } = useQuickBooks();
  const [companyId, setCompanyId] = React.useState(currentCompanyId || '');

  const handleLinkCompany = async () => {
    if (companyId) {
      try {
        await linkClientToCompany(clientId, companyId);
        alert('QuickBooks company linked successfully!');
      } catch (error) {
        console.error('Failed to link company:', error);
      }
    }
  };

  if (!config?.accessToken) {
    return (
      <EmptyState
        title="QuickBooks not connected"
        description="Connect your QuickBooks account in Settings to link companies to projects"
        action={{
          label: 'Go to Settings',
          onClick: () => (window.location.href = '/settings'),
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Link QuickBooks Company</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Connect a QuickBooks company to sync invoices and payments
              </p>
            </div>

            <div className="flex gap-2">
              <Input
                placeholder="Enter QuickBooks Company ID"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
              />
              <Button onClick={handleLinkCompany} disabled={!companyId}>
                <Link2 className="mr-2 h-4 w-4" />
                Link Company
              </Button>
            </div>

            {currentCompanyId && (
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Currently Linked Company</p>
                    <p className="text-sm text-muted-foreground">{currentCompanyId}</p>
                  </div>
                  <Button variant="ghost" size="sm" asChild>
                    <a
                      href={`https://app.qbo.intuit.com/app/company/${currentCompanyId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      View in QuickBooks
                    </a>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recent Invoices Section */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">Recent Invoices</h3>
        <EmptyState
          title="No invoices found"
          description="Invoices from QuickBooks will appear here once synced"
        />
      </div>
    </div>
  );
};
